// src/components/NavBar.js
import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { supabase } from "../supabaseClient";

// One entry per tool. Series selection stays inside the NASCAR pages.
const NASCAR_TOOL_GROUPS = [
  { title: "DATA & SIMS", items: [
    { label: "Driver Data", slug: "driver-data" },
    { label: "DFS Projections", slug: "dfs-projections" },
    { label: "Range of Outcomes", slug: "range-of-outcomes" },
    { label: "Betting Sims", slug: "betting-sims" },
    { label: "Practice Data", slug: "practice-data" },
    { label: "GFS", slug: "gfs" },
  ] },
  { title: "LINEUP TOOLS", items: [
    { label: "Cheat Sheets", slug: "cheatsheets" },
    { label: "Lineup Lab", slug: "lineup-lab" },
    { label: "Optimizer", slug: "optimizer" },
    { label: "Contest Simulator", slug: "contest-simulator" },
  ] },
];

/* ========================== Desktop hover helpers ========================== */
function HoverMenu({ label, children, cols = 1, widthClass = "w-80", onLabelClick }) {
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const gridCols =
    cols === 1 ? "grid-cols-1" : cols === 2 ? "grid-cols-2" : "grid-cols-3";

  const openNow = () => {
    if (timer.current) clearTimeout(timer.current);
    setOpen(true);
  };

  const closeSoon = () => {
    timer.current = setTimeout(() => setOpen(false), 160);
  };

  return (
    <li className="relative" onMouseEnter={openNow} onMouseLeave={closeSoon}>
      <button
        className="px-4 py-2 rounded-md text-slate-50/90 hover:bg-sky-500/20 hover:text-sky-100 focus:outline-none transition"
        type="button"
        onClick={onLabelClick}
      >
        {label}
      </button>

      {open && (
        <div
          className={`absolute left-0 top-full mt-2 z-[60] ${widthClass} rounded-2xl border border-slate-800/80 bg-slate-950/95 shadow-[0_18px_45px_rgba(15,23,42,0.9)] ring-1 ring-sky-500/20 backdrop-blur`}
          onMouseEnter={openNow}
          onMouseLeave={closeSoon}
        >
          <div className={`grid ${gridCols} gap-4 p-3 max-h-[70vh] overflow-y-auto`}>
            {children}
          </div>
        </div>
      )}
    </li>
  );
}

function SectionTitle({ children }) {
  return (
    <div className="sticky top-0 z-10 -mx-3 px-3 pt-2 pb-1 text-[11px] font-extrabold tracking-widest text-sky-300 uppercase bg-slate-900/95 backdrop-blur ring-1 ring-slate-800/80">
      {children}
    </div>
  );
}

function MenuGroup({ children }) {
  return <div className="flex flex-col gap-1">{children}</div>;
}

function MenuItem({ to, children }) {
  return (
    <Link
      to={to}
      className="block px-3 py-2 rounded-md text-[15px] text-slate-200 hover:bg-slate-900/80 hover:text-sky-200 transition"
    >
      {children}
    </Link>
  );
}

/* ============================ Mobile drawer bits =========================== */
function AccordionItem({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="border-b border-slate-800/80">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 font-medium text-slate-100"
        aria-expanded={open}
      >
        <span>{title}</span>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        >
          <path
            d="M6 9l6 6 6-6"
            stroke="currentColor"
            strokeWidth="2"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {open && <div className="pb-2">{children}</div>}
    </div>
  );
}

function MobileLink({ to, children, onClick }) {
  return (
    <NavLink
      to={to}
      onClick={onClick}
      className="block px-8 py-2 text-[15px] text-slate-100 hover:bg-slate-900 rounded-md transition"
    >
      {children}
    </NavLink>
  );
}

/* ================================= NavBar ================================= */
export default function NavBar() {
  const [user, setUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const seriesMatch = pathname.match(/^\/nascar\/(cup|oreilly|xfinity|trucks)(?:\/|$)/);
  const nascarSeries = seriesMatch?.[1] === "xfinity" ? "oreilly" : (seriesMatch?.[1] || "cup");
  const nascarToolPath = (slug) => `/nascar/${nascarSeries}/${slug}`;

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setUser(data.session?.user ?? null);
    });

    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      setUser(session?.user ?? null);
    });

    return () => sub.subscription.unsubscribe();
  }, []);

  const logout = async () => {
    await supabase.auth.signOut();
    window.location.href = "/login";
  };

  const closeMobile = () => setMobileOpen(false);

  return (
    <nav className="sticky top-0 z-50 bg-slate-950 text-white border-b border-slate-800/80 shadow-[0_10px_30px_rgba(15,23,42,0.9)]">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header row */}
        <div className="flex items-center justify-between h-14">
          {/* Brand */}
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/img/logo.png"
              alt="Cpenn DFS"
              className="h-8 w-8 object-contain"
              onError={(e) => (e.currentTarget.style.display = "none")}
            />
            <span className="font-extrabold tracking-wide text-slate-50">
              Cpenn DFS
            </span>
          </Link>

          {/* Desktop menus */}
          <ul className="hidden md:flex items-center gap-2">
            {/* NASCAR */}
            <HoverMenu label="NASCAR" cols={3} widthClass="w-[46rem] max-w-[calc(100vw-2rem)]">
              {NASCAR_TOOL_GROUPS.map((group) => (
                <MenuGroup key={group.title}>
                  <SectionTitle>{group.title}</SectionTitle>
                  {group.items.map(({ label, slug }) => (
                    <MenuItem key={slug} to={nascarToolPath(slug)}>{label}</MenuItem>
                  ))}
                </MenuGroup>
              ))}
              <MenuGroup>
                <SectionTitle>RESEARCH</SectionTitle>
                <MenuItem to="/nascar/research/articles">Articles</MenuItem>
                <MenuItem to="/nascar/research/loop-data-explorer">Loop Data Explorer</MenuItem>
                <MenuItem to="/nascar/research/race-hub">Race Hub</MenuItem>
                <MenuItem to="/nascar/research/track-data">Track Data</MenuItem>
                <MenuItem to="/nascar/research/contest-dashboard">Contest Dashboard</MenuItem>
                <div className="mt-3"><SectionTitle>F1</SectionTitle></div>
                <MenuItem to="/nascar/f1/projections">F1 Projections</MenuItem>
                <MenuItem to="/nascar/f1/optimizer">F1 Optimizer</MenuItem>
              </MenuGroup>
            </HoverMenu>

            {/* NFL */}
            <HoverMenu label="NFL" cols={3} widthClass="w-[46rem]">
              <MenuGroup>
                <SectionTitle>CLASSIC</SectionTitle>
                <MenuItem to="/nfl/classic/projections">DFS Projections</MenuItem>
                <MenuItem to="/nfl/classic/player-projections">Player Projections</MenuItem>
                <MenuItem to="/nfl/classic/player-data/qb">Player Data</MenuItem>
                <MenuItem to="/nfl/classic/range-of-outcomes">Range of Outcomes</MenuItem>
                <MenuItem to="/nfl/classic/stacks">Stacks</MenuItem>
                <MenuItem to="/nfl/classic/top-plays">Top Plays</MenuItem>
                <MenuItem to="/nfl/classic/cheatsheets">Cheat Sheets</MenuItem>
                <MenuItem to="/nfl/classic/lineup-lab">Lineup Lab</MenuItem>
                <MenuItem to="/nfl/classic/contest-simulator">Contest Simulator</MenuItem>
                <MenuItem to="/nfl/classic/optimizer">Optimizer</MenuItem>
              </MenuGroup>

              <MenuGroup>
                <SectionTitle>SHOWDOWN</SectionTitle>
                <MenuItem to="/nfl/showdown/projections">DFS Projections</MenuItem>
                <MenuItem to="/nfl/showdown/player-projections">Player Projections</MenuItem>
                <MenuItem to="/nfl/showdown/player-data/qb">Player Data</MenuItem>
                <MenuItem to="/nfl/showdown/lineup-lab">Lineup Lab</MenuItem>
                <MenuItem to="/nfl/showdown/optimizer">Optimizer</MenuItem>

                <div className="mt-3">
                  <SectionTitle>SEASON LONG</SectionTitle>
                </div>
                <MenuItem to="/nfl/season-long/rankings">Fantasy Rankings</MenuItem>
              </MenuGroup>

              <MenuGroup>
                <SectionTitle>RESEARCH</SectionTitle>
                <MenuItem to="/nfl/classic/advanced-stats">Advanced Stats</MenuItem>
                <MenuItem to="/nfl/classic/simulations">Simulations</MenuItem>
                <MenuItem to="/nfl/classic/team-projections">Team Projections</MenuItem>
                <MenuItem to="/nfl/classic/weather">Weather</MenuItem>
              </MenuGroup>
            </HoverMenu>

            {/* MLB */}
            <HoverMenu label="MLB" cols={2} widthClass="w-[36rem]">
              <MenuGroup>
                <SectionTitle>DAILY DATA</SectionTitle>
                <MenuItem to="/mlb/projections">DFS Projections</MenuItem>
                <MenuItem to="/mlb/range-of-outcomes">Range of Outcomes</MenuItem>
                <MenuItem to="/mlb/player-projections">Player Projections</MenuItem>
                <MenuItem to="/mlb/player-data">Player Data</MenuItem>
                <MenuItem to="/mlb/stacks">Stacks</MenuItem>
                <MenuItem to="/mlb/cheatsheets">Cheat Sheets</MenuItem>
                <MenuItem to="/mlb/optimizer">Optimizer</MenuItem>
              </MenuGroup>

              <MenuGroup>
                <SectionTitle>RESEARCH</SectionTitle>
                <MenuItem to="/mlb/advanced-stats">Advanced Stats</MenuItem>
                <MenuItem to="/mlb/simulations">Simulations</MenuItem>
                <MenuItem to="/mlb/lineups">Lineups</MenuItem>
                <MenuItem to="/mlb/weather">Weather</MenuItem>
              </MenuGroup>
            </HoverMenu>

{/* NBA */}
<HoverMenu label="NBA" cols={2} widthClass="w-[36rem]">
  <MenuGroup>
    <SectionTitle>DAILY DATA</SectionTitle>
    <MenuItem to="/nba/data">Player Data</MenuItem>
    <MenuItem to="/nba/projections">DFS Projections</MenuItem>
    <MenuItem to="/nba/cheatsheets">Cheat Sheets</MenuItem>
    <MenuItem to="/nba/optimizer">Optimizer</MenuItem>
  </MenuGroup>

  <MenuGroup>
    <SectionTitle>RESEARCH</SectionTitle>
    <MenuItem to="/nba/advanced-stats">Advanced Stats</MenuItem>
  </MenuGroup>
</HoverMenu>
          </ul>

          {/* Right side desktop */}
          <div className="hidden md:flex items-center gap-2">
            <NavLink
              to="/"
              className={({ isActive }) =>
                `px-3 py-1.5 rounded-md ${isActive ? "bg-blue-700" : "hover:bg-blue-700"}`
              }
            >
              Home
            </NavLink>

            <NavLink
              to="/pricing"
              className={({ isActive }) =>
                `px-3 py-1.5 rounded-md ${isActive ? "bg-blue-700" : "hover:bg-blue-700"}`
              }
            >
              Pricing
            </NavLink>

            <NavLink
              to="/info"
              className={({ isActive }) =>
                `px-3 py-1.5 rounded-md ${isActive ? "bg-blue-700" : "hover:bg-blue-700"}`
              }
            >
              Info
            </NavLink>

            <span className="mx-1 h-6 w-px bg-white/20" />

            {!user ? (
              <>
                <NavLink
                  to="/login"
                  className={({ isActive }) =>
                    `px-3 py-1.5 rounded-md ${isActive ? "bg-blue-700" : "hover:bg-blue-700"}`
                  }
                >
                  Login
                </NavLink>

                <Link
                  to="/signup"
                  className="ml-1 rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-blue-900 hover:bg-gray-100"
                >
                  Sign Up
                </Link>
              </>
            ) : (
              <>
                <NavLink
                  to="/account"
                  className={({ isActive }) =>
                    `px-3 py-1.5 rounded-md ${isActive ? "bg-blue-700" : "hover:bg-blue-700"}`
                  }
                >
                  Account
                </NavLink>

                <button
                  onClick={logout}
                  className="px-3 py-1.5 rounded-md hover:bg-blue-700"
                >
                  Logout
                </button>
              </>
            )}
          </div>

          {/* Hamburger mobile */}
          <button
            className="md:hidden inline-flex items-center justify-center rounded-md p-2 hover:bg-blue-800"
            aria-label="Open menu"
            onClick={() => setMobileOpen(true)}
          >
            <svg width="24" height="24" viewBox="0 0 24 24">
              <path
                d="M4 6h16M4 12h16M4 18h16"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMobileOpen(false)}
            aria-hidden="true"
          />

          <div className="absolute right-0 top-0 h-full w-[92%] max-w-sm bg-slate-950 text-slate-100 shadow-[0_18px_45px_rgba(15,23,42,0.9)] border-l border-slate-800/80">
            <div className="flex items-center justify-between px-4 h-14 border-b border-slate-800/80">
              <span className="font-bold">Menu</span>

              <button
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
                className="p-2 rounded-md hover:bg-slate-900"
              >
                <svg width="22" height="22" viewBox="0 0 24 24">
                  <path
                    d="M6 6l12 12M18 6l-12 12"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </div>

            <div className="px-2 py-3 space-y-1 overflow-y-auto h-[calc(100%-3.5rem)]">
              <NavLink
                to="/"
                onClick={closeMobile}
                className="block px-4 py-2 rounded-md hover:bg-slate-900"
              >
                Home
              </NavLink>

              <NavLink
                to="/pricing"
                onClick={closeMobile}
                className="block px-4 py-2 rounded-md hover:bg-slate-900"
              >
                Pricing
              </NavLink>

              <NavLink
                to="/info"
                onClick={closeMobile}
                className="block px-4 py-2 rounded-md hover:bg-slate-900"
              >
                Info
              </NavLink>

              <div className="my-2 border-t border-slate-800/80" />

              {!user ? (
                <>
                  <NavLink
                    to="/login"
                    onClick={closeMobile}
                    className="block px-4 py-2 rounded-md hover:bg-slate-900"
                  >
                    Login
                  </NavLink>

                  <NavLink
                    to="/signup"
                    onClick={closeMobile}
                    className="block px-4 py-2 rounded-md hover:bg-slate-900"
                  >
                    Sign Up
                  </NavLink>
                </>
              ) : (
                <>
                  <NavLink
                    to="/account"
                    onClick={closeMobile}
                    className="block px-4 py-2 rounded-md hover:bg-slate-900"
                  >
                    Account
                  </NavLink>

                  <button
                    onClick={() => {
                      closeMobile();
                      logout();
                    }}
                    className="w-full text-left px-4 py-2 rounded-md hover:bg-slate-900"
                  >
                    Logout
                  </button>
                </>
              )}

              <div className="my-2 border-t border-slate-800/80" />

              <div className="rounded-lg border border-slate-800/80 overflow-hidden bg-slate-950/90">
                {/* NASCAR */}
                <AccordionItem title="NASCAR" defaultOpen={false}>
                  {NASCAR_TOOL_GROUPS.map((group) => (
                    <div key={group.title}>
                      <div className="px-4 pt-3 pb-1 text-xs font-bold tracking-widest text-slate-400">{group.title}</div>
                      {group.items.map(({ label, slug }) => (
                        <MobileLink key={slug} to={nascarToolPath(slug)} onClick={closeMobile}>{label}</MobileLink>
                      ))}
                    </div>
                  ))}
                  <div className="px-4 pt-4 pb-1 text-xs font-bold tracking-widest text-slate-400">RESEARCH</div>
                  <MobileLink to="/nascar/research/articles" onClick={closeMobile}>Articles</MobileLink>
                  <MobileLink to="/nascar/research/loop-data-explorer" onClick={closeMobile}>Loop Data Explorer</MobileLink>
                  <MobileLink to="/nascar/research/race-hub" onClick={closeMobile}>Race Hub</MobileLink>
                  <MobileLink to="/nascar/research/track-data" onClick={closeMobile}>Track Data</MobileLink>
                  <MobileLink to="/nascar/research/contest-dashboard" onClick={closeMobile}>Contest Dashboard</MobileLink>
                  <div className="px-4 pt-4 pb-1 text-xs font-bold tracking-widest text-slate-400">F1</div>
                  <MobileLink to="/nascar/f1/projections" onClick={closeMobile}>F1 Projections</MobileLink>
                  <MobileLink to="/nascar/f1/optimizer" onClick={closeMobile}>F1 Optimizer</MobileLink>
                </AccordionItem>

                {/* NFL */}
                <AccordionItem title="NFL">
                  <div className="px-4 py-1 text-xs font-bold tracking-widest text-slate-400">
                    CLASSIC
                  </div>

                  <MobileLink to="/nfl/classic/projections" onClick={closeMobile}>
                    DFS Projections
                  </MobileLink>
                  <MobileLink to="/nfl/classic/player-projections" onClick={closeMobile}>
                    Player Projections
                  </MobileLink>
                  <MobileLink to="/nfl/classic/player-data/qb" onClick={closeMobile}>
                    Player Data
                  </MobileLink>
                  <MobileLink to="/nfl/classic/range-of-outcomes" onClick={closeMobile}>
                    Range of Outcomes
                  </MobileLink>
                  <MobileLink to="/nfl/classic/stacks" onClick={closeMobile}>
                    Stacks
                  </MobileLink>
                  <MobileLink to="/nfl/classic/top-plays" onClick={closeMobile}>
                    Top Plays
                  </MobileLink>
                  <MobileLink to="/nfl/classic/cheatsheets" onClick={closeMobile}>
                    Cheat Sheets
                  </MobileLink>
                  <MobileLink to="/nfl/classic/lineup-lab" onClick={closeMobile}>
                    Lineup Lab
                  </MobileLink>
                  <MobileLink
                      to="/nfl/classic/contest-simulator"
                      onClick={closeMobile}
                    >
                      Contest Simulator
                    </MobileLink>
                  <MobileLink to="/nfl/classic/optimizer" onClick={closeMobile}>
                    Optimizer
                  </MobileLink>

                  <div className="px-4 pt-3 pb-1 text-xs font-bold tracking-widest text-slate-400">
                    SHOWDOWN
                  </div>

                  <MobileLink to="/nfl/showdown/projections" onClick={closeMobile}>
                    DFS Projections
                  </MobileLink>
                  <MobileLink to="/nfl/showdown/player-projections" onClick={closeMobile}>
                    Player Projections
                  </MobileLink>
                  <MobileLink to="/nfl/showdown/player-data/qb" onClick={closeMobile}>
                    Player Data
                  </MobileLink>
                  <MobileLink to="/nfl/showdown/lineup-lab" onClick={closeMobile}>
                    Lineup Lab
                  </MobileLink>
                  <MobileLink to="/nfl/showdown/optimizer" onClick={closeMobile}>
                    Optimizer
                  </MobileLink>

                  <div className="px-4 pt-3 pb-1 text-xs font-bold tracking-widest text-slate-400">
                    SEASON LONG
                  </div>

                  <MobileLink to="/nfl/season-long/rankings" onClick={closeMobile}>
                    Fantasy Rankings
                  </MobileLink>

                  <div className="px-4 pt-3 pb-1 text-xs font-bold tracking-widest text-slate-400">
                    RESEARCH
                  </div>

                  <MobileLink to="/nfl/classic/advanced-stats" onClick={closeMobile}>
                    Advanced Stats
                  </MobileLink>
                  <MobileLink to="/nfl/classic/simulations" onClick={closeMobile}>
                    Simulations
                  </MobileLink>
                  <MobileLink to="/nfl/classic/team-projections" onClick={closeMobile}>
                    Team Projections
                  </MobileLink>
                  <MobileLink to="/nfl/classic/weather" onClick={closeMobile}>
                    Weather
                  </MobileLink>
                </AccordionItem>

                {/* MLB */}
                <AccordionItem title="MLB">
                  <div className="px-4 pt-3 pb-1 text-xs font-bold tracking-widest text-slate-400">
                    DAILY DATA
                  </div>

                  <MobileLink to="/mlb/projections" onClick={closeMobile}>
                    DFS Projections
                  </MobileLink>
                  <MobileLink to="/mlb/range-of-outcomes" onClick={closeMobile}>
                    Range of Outcomes
                  </MobileLink>
                  <MobileLink to="/mlb/player-projections" onClick={closeMobile}>
                    Player Projections
                  </MobileLink>
                  <MobileLink to="/mlb/player-data" onClick={closeMobile}>
                    Player Data
                  </MobileLink>
                  <MobileLink to="/mlb/stacks" onClick={closeMobile}>
                    Stacks
                  </MobileLink>
                  <MobileLink to="/mlb/cheatsheets" onClick={closeMobile}>
                    Cheat Sheets
                  </MobileLink>
                  <MobileLink to="/mlb/optimizer" onClick={closeMobile}>
                    Optimizer
                  </MobileLink>

                  <div className="px-4 pt-3 pb-1 text-xs font-bold tracking-widest text-slate-400">
                    RESEARCH
                  </div>

                  <MobileLink to="/mlb/advanced-stats" onClick={closeMobile}>
                    Advanced Stats
                  </MobileLink>
                  <MobileLink to="/mlb/simulations" onClick={closeMobile}>
                    Simulations
                  </MobileLink>
                  <MobileLink to="/mlb/lineups" onClick={closeMobile}>
                    Lineups
                  </MobileLink>
                  <MobileLink to="/mlb/weather" onClick={closeMobile}>
                    Weather
                  </MobileLink>
                </AccordionItem>

{/* NBA */}
<AccordionItem title="NBA">
  <div className="px-4 pt-3 pb-1 text-xs font-bold tracking-widest text-slate-400">
    DAILY DATA
  </div>

  <MobileLink to="/nba/data" onClick={closeMobile}>
    Player Data
  </MobileLink>
  <MobileLink to="/nba/projections" onClick={closeMobile}>
    DFS Projections
  </MobileLink>
  <MobileLink to="/nba/cheatsheets" onClick={closeMobile}>
    Cheat Sheets
  </MobileLink>
  <MobileLink to="/nba/optimizer" onClick={closeMobile}>
    Optimizer
  </MobileLink>

  <div className="px-4 pt-3 pb-1 text-xs font-bold tracking-widest text-slate-400">
    RESEARCH
  </div>

  <MobileLink to="/nba/advanced-stats" onClick={closeMobile}>
    Advanced Stats
  </MobileLink>
</AccordionItem>
              </div>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}